import { unwrap, type Api } from "../api/index.js";
import type { PartnerDeclaration } from "@kovernow/types/tenant-web-config";
import type { ItemUploadDocument } from "../uploads/index.js";
import { withDocuments, type ItemUpdatePayload } from "../vault/item_detail.js";
import type { ItemDocument, VaultItem } from "../vault/index.js";
import {
  buildCollectorAttributes,
  collectorItemName,
  collectorSections,
  isCollectorCategory,
  sanitizeCollectorValues,
  validateCollectorSection,
  type CollectorCategory,
  type CollectorCreatePayload,
  type CollectorSectionId,
  type CollectorValues,
} from "./collector.js";

export * from "./collector.js";
import { parseAmount } from "./amount.js";
export { parseAmount };

/**
 * Add item, headless: the five legacy categories (watch, handbag,
 * jewellery, camera, laptop) and the collector categories (art, wine,
 * whisky, trading card; schemas in `./collector.ts`). The API calls, query
 * keys, URL params and the step guard, the details/condition rules the
 * backend also enforces (`POST /items`), the create payload, and text-only
 * drafts.
 *
 * Legacy steps: category → search → details → condition → photos. Collector
 * steps: category → each schema section (info, grading, storage,
 * documents). Carried in the URL as `?category=<c>&step=<step>`.
 */

// ---------------------------------------------------------------- constants

/** Legacy classification → item-database `classId` (as `AssetType`). */
export const LEGACY_CLASS_IDS = {
  watch: 4,
  laptop: 2,
  handbag: 3,
  camera: 5,
  jewellery: 6,
} as const;

export type LegacyCategory = keyof typeof LEGACY_CLASS_IDS;

export function isLegacyCategory(c: string | null | undefined): c is LegacyCategory {
  return !!c && Object.prototype.hasOwnProperty.call(LEGACY_CLASS_IDS, c);
}

export const ADD_ITEM_STEPS = ["category", "search", "details", "condition", "photos"] as const;
export type LegacyStep = (typeof ADD_ITEM_STEPS)[number];
export type AddItemStep = LegacyStep | CollectorSectionId;

/** Any category the add-item flow handles. */
export type AddCategory = LegacyCategory | CollectorCategory;

export function isAddCategory(c: string | null | undefined): c is AddCategory {
  return isLegacyCategory(c) || isCollectorCategory(c);
}

/** The steps for a category (no category: the legacy list). */
export function stepsFor(category: AddCategory | null): readonly AddItemStep[] {
  return isCollectorCategory(category) ? ["category", ...collectorSections(category)] : ADD_ITEM_STEPS;
}

/** Stored as the label (as the app does). */
export const ITEM_CONDITIONS = ["Brand new", "Like new", "Very good", "Good", "Acceptable"] as const;
export type ItemCondition = (typeof ITEM_CONDITIONS)[number];
export const DEFAULT_CONDITION: ItemCondition = "Brand new";

/** i18n key per condition label. */
export const CONDITION_KEYS: Record<ItemCondition, string> = {
  "Brand new": "addItem.condition.options.brandNew",
  "Like new": "addItem.condition.options.likeNew",
  "Very good": "addItem.condition.options.veryGood",
  Good: "addItem.condition.options.good",
  Acceptable: "addItem.condition.options.acceptable",
};

/** Stored in `listAccessory` as these labels. */
export const ACCESSORIES = ["Original Box", "Original Papers", "Certificate of authenticity"] as const;
export type Accessory = (typeof ACCESSORIES)[number];

export const ACCESSORY_KEYS: Record<Accessory, string> = {
  "Original Box": "addItem.condition.accessory.box",
  "Original Papers": "addItem.condition.accessory.papers",
  "Certificate of authenticity": "addItem.condition.accessory.certificate",
};

export const SEARCH_PAGE_SIZE = 20;
export const SEARCH_DEBOUNCE_MS = 300;
export const MAX_PURCHASE_AGE_DAYS = 7300;
/** The PAR002 window notice shows for purchases more than 30 days ago. */
export const PARTNER_WINDOW_DAYS = 30;
const DAY_MS = 86_400_000;

// ---------------------------------------------------------------- API

export const addItemQueryKeys = {
  all: ["addItem"] as const,
  account: () => ["addItem", "account"] as const,
  brands: (classId: number) => ["addItem", "brands", classId] as const,
  search: (classId: number, q: string) => ["addItem", "search", classId, q] as const,
  advanced: (classId: number, f: AdvancedSearchFields) =>
    ["addItem", "advanced", classId, f.brand, f.type, f.model] as const,
};

export interface SearchResult {
  /** `objectID`, else `_id`: the item's `universalId`. */
  universalId: string;
  brand: string;
  model: string;
  type: string;
}

export interface SearchPage {
  results: SearchResult[];
  hasNextPage: boolean;
  page: number;
}

export interface AdvancedSearchFields {
  brand: string;
  type: string;
  model: string;
}

export interface AddItemAccount {
  id: string;
  partnerId?: string;
}

export type ItemCurrency = "SGD" | "HKD" | "USD" | "AUD";

export interface LegacyCreatePayload {
  universalId?: string;
  classId: number;
  classification: LegacyCategory;
  brand: string;
  model: string;
  type: string;
  notes?: string;
  isBrand: boolean;
  condition: ItemCondition;
  valueAmount: number;
  valueCurrency: ItemCurrency;
  purchasedAt: string;
  documents: ItemUploadDocument[];
  listAccessory: string[];
  isPartnerItem?: boolean;
}

/** `POST /items`: a legacy item, or a collector item with its `attributes`. */
export type CreateItemPayload = LegacyCreatePayload | CollectorCreatePayload;

export interface CreatedItem {
  _id: string;
  /** e.g. `pending_underwriter_review` when the evaluator flags it. */
  status?: string;
}

export interface AddItemApi {
  /** GET /items/search (openapi-fetch encodes the query). */
  search(classId: number, searchString: string, page: number): Promise<SearchPage>;
  /** GET /items/advanceSearch with only the filled fields. */
  advanceSearch(classId: number, fields: AdvancedSearchFields, page: number): Promise<SearchPage>;
  /** GET /items/listBrands. */
  listBrands(classId: number): Promise<string[]>;
  /** POST /uploads/token. */
  uploadToken(): Promise<{ token: string; pathPrefix: string }>;
  /** POST /items. */
  create(payload: CreateItemPayload): Promise<CreatedItem>;
  /** GET /users/: the caller's id and partnerId. */
  account(): Promise<AddItemAccount>;
}

interface SearchNode {
  _id: string;
  objectID?: string;
  brand: string;
  model: string;
  type: string;
}

export function toSearchResult(node: SearchNode): SearchResult {
  return {
    universalId: node.objectID || node._id,
    brand: node.brand ?? "",
    model: node.model ?? "",
    type: node.type ?? "",
  };
}

/** Advanced-search query: only fields with text, trimmed. */
export function advancedQuery(fields: AdvancedSearchFields): Partial<AdvancedSearchFields> {
  const out: Partial<AdvancedSearchFields> = {};
  for (const k of ["brand", "type", "model"] as const) {
    const v = fields[k].trim();
    if (v) out[k] = v;
  }
  return out;
}

export function createAddItemApi(api: Api): AddItemApi {
  const c = api.shared;
  const page = (
    r: { data?: { nodes: SearchNode[]; pageInfo: { hasNextPage: boolean; page: number } } },
    p: number,
  ): SearchPage => ({
    results: (r.data?.nodes ?? []).map(toSearchResult),
    hasNextPage: r.data?.pageInfo.hasNextPage ?? false,
    page: r.data?.pageInfo.page || p,
  });
  return {
    async search(classId, searchString, p) {
      const r = await unwrap(
        c.GET("/api/sg/items/search", {
          params: { query: { classId, searchString, page: p, limit: SEARCH_PAGE_SIZE } },
        }),
      );
      return page(r, p);
    },
    async advanceSearch(classId, fields, p) {
      const r = await unwrap(
        c.GET("/api/sg/items/advanceSearch", {
          params: { query: { classId, page: p, limit: SEARCH_PAGE_SIZE, ...advancedQuery(fields) } },
        }),
      );
      return page(r, p);
    },
    async listBrands(classId) {
      const r = await unwrap(c.GET("/api/sg/items/listBrands", { params: { query: { classId } } }));
      return r.data;
    },
    async uploadToken() {
      const r = await unwrap(c.POST("/api/sg/uploads/token", {}));
      return { token: r.data.token, pathPrefix: r.data.pathPrefix };
    },
    async create(payload) {
      const r = await unwrap(
        // The DTO types `documents[].type` and `attributes` loosely; both payload shapes fit it.
        c.POST("/api/sg/items/", { body: payload as never }),
      );
      return { _id: String(r.data._id), status: r.data.status };
    },
    async account() {
      const r = await unwrap(c.GET("/api/sg/users/", {}));
      return { id: String(r.data._id), partnerId: r.data.partnerId };
    },
  };
}

// ---------------------------------------------------------------- form state

export interface AddItemFields {
  category: AddCategory | null;
  /** How details were reached: a picked search result, or manual entry. */
  entry: "picked" | "manual" | null;
  universalId?: string;
  /** Manual entry only: "brand" (default) or "bespoke". */
  brandKind: "brand" | "bespoke";
  brand: string;
  model: string;
  type: string;
  /** Bespoke: "Describe your item", stored as `notes`. */
  notes: string;
  /** Purchase value as typed. */
  value: string;
  /** Purchase date, `YYYY-MM-DD`. */
  date: string;
  condition: ItemCondition;
  accessories: Accessory[];
  isPartnerItem: boolean;
  /** Collector form values (text and choices only; see `./collector.ts`). */
  attributes: CollectorValues;
}

export const EMPTY_FIELDS: AddItemFields = {
  category: null,
  entry: null,
  brandKind: "brand",
  brand: "",
  model: "",
  type: "",
  notes: "",
  value: "",
  date: "",
  condition: DEFAULT_CONDITION,
  accessories: [],
  isPartnerItem: false,
  attributes: {},
};

/** Picking a search result pre-fills (and locks) brand, model and type. */
export function pickResult(fields: AddItemFields, r: SearchResult): AddItemFields {
  return {
    ...fields,
    entry: "picked",
    universalId: r.universalId,
    brandKind: "brand",
    brand: r.brand,
    model: r.model,
    type: r.type,
    notes: "",
  };
}

/** "Can't find it?": a blank details step. */
export function startManual(fields: AddItemFields): AddItemFields {
  return { ...fields, entry: "manual", universalId: undefined, brandKind: "brand", brand: "", model: "", type: "", notes: "" };
}

/** "Change": back to search, the picked result cleared. */
export function clearPick(fields: AddItemFields): AddItemFields {
  return { ...fields, entry: null, universalId: undefined, brand: "", model: "", type: "" };
}

// ---------------------------------------------------------------- params + guard

export interface AddItemParams {
  category: AddCategory | null;
  step: AddItemStep;
}

/**
 * `?category=<c>&step=<s>`. A category that isn't one of the tenant's
 * offered categories (`allowed`, e.g. a collector behind an off flag, or
 * unknown) is dropped. A valid category with no step (a Home tile) starts
 * at search (legacy) or `info` (collector: there is no search step). A step
 * that isn't one of the category's steps is ignored.
 */
export function parseAddItemParams(
  search: URLSearchParams | string,
  allowed: readonly string[],
): AddItemParams {
  const p = typeof search === "string" ? new URLSearchParams(search) : search;
  const c = p.get("category");
  const category = isAddCategory(c) && allowed.includes(c) ? c : null;
  if (!category) return { category: null, step: "category" };
  const s = p.get("step");
  const steps = stepsFor(category);
  const requested = (steps as readonly string[]).includes(s ?? "") ? (s as AddItemStep) : null;
  return { category, step: requested ?? steps[1]! };
}

export function toAddItemParams(params: AddItemParams): URLSearchParams {
  const out = new URLSearchParams();
  if (params.category) out.set("category", params.category);
  out.set("step", params.step);
  return out;
}

export function stepNumber(step: AddItemStep, category: AddCategory | null = null): number {
  return stepsFor(category).indexOf(step) + 1;
}

/** "Step n of {total}": 5 for legacy, 1 + the sections for a collector. */
export function stepTotal(category: AddCategory | null): number {
  return stepsFor(category).length;
}

/** The first step whose earlier steps are complete, at most `requested`. */
export function guardStep(requested: AddItemStep, fields: AddItemFields, ctx: ValidateContext): AddItemStep {
  const category = fields.category;
  if (isCollectorCategory(category)) {
    const steps = stepsFor(category);
    const want = Math.max(0, steps.indexOf(requested));
    for (let i = 1; i < want; i++) {
      const s = steps[i] as CollectorSectionId;
      const errors = validateCollectorSection(category, s, fields.attributes, {
        today: ctx.today,
        currency: ctx.currency ?? "",
      });
      if (Object.keys(errors).length) return s;
    }
    return steps[want]!;
  }
  if (!(ADD_ITEM_STEPS as readonly string[]).includes(requested)) return category ? "search" : "category";
  const want = ADD_ITEM_STEPS.indexOf(requested as LegacyStep);
  const done: Record<LegacyStep, boolean> = {
    category: !!fields.category,
    search: !!fields.entry,
    details: Object.keys(validateDetails(fields, ctx)).length === 0,
    condition: (ITEM_CONDITIONS as readonly string[]).includes(fields.condition),
    photos: true,
  };
  for (let i = 0; i < want; i++) {
    const s = ADD_ITEM_STEPS[i]!;
    if (!done[s]) return s;
  }
  return requested;
}

// ---------------------------------------------------------------- validation

export interface ValidateContext {
  /** Now (for the 20-year rule, computed in UTC as the backend does). */
  now: Date;
  /** Today in the tenant time zone, `YYYY-MM-DD` (for "not in the future"). */
  today: string;
  /** The tenant currency (collector items: the trading-card cap). */
  currency?: string;
}

/** `YYYY-MM-DD` in a time zone. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` → UTC midnight ms, or null. */
export function parseDate(value: string): number | null {
  const m = ISO_DATE.exec(value.trim());
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(ms);
  if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) {
    return null;
  }
  return ms;
}

/** Whole days between the purchase (UTC midnight) and now, truncated like dayjs `diff`. */
export function purchaseAgeDays(date: string, now: Date): number | null {
  const ms = parseDate(date);
  return ms === null ? null : Math.trunc((now.getTime() - ms) / DAY_MS);
}


export type DetailsField = "brand" | "value" | "date";
export type DetailsErrors = Partial<Record<DetailsField, string>>;

/** Field → i18n key of its error. Model and Type are optional. */
export function validateDetails(fields: AddItemFields, ctx: ValidateContext): DetailsErrors {
  const errors: DetailsErrors = {};
  if (isBrandItem(fields) && !fields.brand.trim()) errors.brand = "addItem.errors.brandRequired";
  const amount = parseAmount(fields.value);
  if (amount === null || !(amount > 0)) errors.value = "addItem.errors.value";
  if (!fields.date.trim()) {
    errors.date = "addItem.errors.dateRequired";
  } else {
    const age = purchaseAgeDays(fields.date, ctx.now);
    if (age === null) errors.date = "addItem.errors.dateRequired";
    else if (fields.date > ctx.today) errors.date = "addItem.errors.dateFuture";
    else if (age > MAX_PURCHASE_AGE_DAYS) errors.date = "addItem.errors.dateOld";
  }
  return errors;
}

/** A picked result is branded; manual entry follows the Brand/Bespoke radio. */
export function isBrandItem(fields: Pick<AddItemFields, "entry" | "brandKind">): boolean {
  return fields.entry === "picked" || fields.brandKind === "brand";
}

export type PhotosError = "addItem.errors.photosMin";

export function validatePhotos(photoCount: number): PhotosError | null {
  return photoCount < 1 ? "addItem.errors.photosMin" : null;
}

// ---------------------------------------------------------------- hints + PAR002

export function showsMinPremiumHint(category: string | null): boolean {
  return category === "watch" || category === "handbag" || category === "jewellery";
}

export function showsWatchValueHint(category: string | null): boolean {
  return category === "watch";
}

/** The tenant declaration for this customer and category, if any. */
export function partnerDeclarationFor(
  declarations: readonly PartnerDeclaration[] | undefined,
  partnerId: string | undefined,
  category: string | null,
): PartnerDeclaration | undefined {
  if (!partnerId || !category) return undefined;
  return (declarations ?? []).find(
    (d) => d.partnerId === partnerId && (d.categories as readonly string[]).includes(category),
  );
}

/** On, and bought more than 30 days ago. */
export function showsPartnerWindowNotice(fields: Pick<AddItemFields, "isPartnerItem" | "date">, now: Date): boolean {
  if (!fields.isPartnerItem) return false;
  const age = purchaseAgeDays(fields.date, now);
  return age !== null && age > PARTNER_WINDOW_DAYS;
}

// ---------------------------------------------------------------- payload

export function buildCreatePayload(
  fields: AddItemFields,
  opts: {
    currency: ItemCurrency;
    documents: ItemUploadDocument[];
    /** The PAR002 declaration was shown; otherwise `isPartnerItem` is omitted. */
    partnerDeclarationShown: boolean;
  },
): LegacyCreatePayload {
  if (!isLegacyCategory(fields.category)) throw new Error("buildCreatePayload: not a legacy category");
  const branded = isBrandItem(fields);
  const payload: LegacyCreatePayload = {
    classId: LEGACY_CLASS_IDS[fields.category],
    classification: fields.category,
    brand: branded ? fields.brand.trim() : "",
    model: branded ? fields.model.trim() : "",
    type: fields.type.trim(),
    isBrand: branded,
    condition: fields.condition,
    valueAmount: parseAmount(fields.value) ?? 0,
    valueCurrency: opts.currency,
    purchasedAt: `${fields.date}T00:00:00.000Z`,
    documents: opts.documents,
    listAccessory: [...fields.accessories],
  };
  if (fields.entry === "picked" && fields.universalId) payload.universalId = fields.universalId;
  if (!branded && fields.notes.trim()) payload.notes = fields.notes.trim();
  if (opts.partnerDeclarationShown) payload.isPartnerItem = fields.isPartnerItem;
  return payload;
}

/** The name announced after adding ("{name} added to your vault"). */
export function addedItemName(fields: AddItemFields): string {
  if (isCollectorCategory(fields.category)) {
    return collectorItemName(fields.category, buildCollectorAttributes(fields.category, fields.attributes)) ?? "";
  }
  if (isBrandItem(fields)) return [fields.brand.trim(), fields.model.trim()].filter(Boolean).join(" ");
  return fields.notes.trim();
}

// ---------------------------------------------------------------- server codes

/**
 * `POST /items` codes that point at a field, and the step that owns it.
 * `insuredAmount` is resolved by the screen to the field holding the insured
 * amount (`insuredAmountField`).
 */
export const CREATE_ERROR_FIELDS: Record<
  string,
  { step: AddItemStep; field: DetailsField | "photos" | "condition" | "insuredAmount" }
> = {
  brand_required: { step: "details", field: "brand" },
  invalid_value_amount: { step: "details", field: "value" },
  invalid_purchase_date: { step: "details", field: "date" },
  invalid_condition: { step: "condition", field: "condition" },
  invalid_image_count: { step: "photos", field: "photos" },
  tradingcard_value_exceeds_max: { step: "info", field: "insuredAmount" },
};

/** Every code `POST /items` can return for the add-item rules (each has `errors.<code>`). */
export const CREATE_ERROR_CODES = [
  "invalid_image_count",
  "brand_required",
  "invalid_value_amount",
  "invalid_purchase_date",
  "invalid_condition",
  "invalid_document_location",
  "invalid_class_id",
  // Collector items
  "tradingcard_value_exceeds_max",
  "invalid_attributes_shape",
  "classification_not_available",
  "feature_not_enabled",
  "collector_categories_sgd_only",
  "collector_categories_hkd_only",
  "invalid_document_type",
  "too_many_documents",
] as const;

// ---------------------------------------------------------------- edit (W-6)

/** A legacy item's edit steps (`/vault/:id/edit?step=`): no category or search step. */
export const LEGACY_EDIT_STEPS = ["details", "condition", "photos"] as const;
export type LegacyEditStep = (typeof LEGACY_EDIT_STEPS)[number];

/** `?step=` for the legacy edit route: unknown → details. */
export function parseLegacyEditStep(step: string | null | undefined): LegacyEditStep {
  return (LEGACY_EDIT_STEPS as readonly string[]).includes(step ?? "") ? (step as LegacyEditStep) : "details";
}

/** The first edit step whose earlier steps are invalid, at most `requested`. */
export function guardLegacyEditStep(requested: LegacyEditStep, fields: AddItemFields, ctx: ValidateContext): LegacyEditStep {
  if (requested !== "details" && Object.keys(validateDetails(fields, ctx)).length > 0) return "details";
  return requested;
}

/** `YYYY-MM-DD` of a stored `purchasedAt`, in UTC (as `dayjs.utc(x).format`), or "". */
export function utcDay(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

/**
 * A legacy item's form fields for edit: the value as plain dollars, the
 * purchase date as a UTC day, condition and accessories carried over. A
 * picked item (`universalId`) keeps brand, model and type locked; `isBrand:
 * false` restores the bespoke path with its notes.
 */
export function prefillLegacyFromItem(item: VaultItem): AddItemFields {
  const category = isLegacyCategory(item.classification) ? item.classification : null;
  const picked = !!item.universalId;
  const value = typeof item.valueAmount === "number" && Number.isFinite(item.valueAmount) ? String(item.valueAmount) : "";
  return {
    ...EMPTY_FIELDS,
    category,
    entry: picked ? "picked" : "manual",
    universalId: item.universalId || undefined,
    brandKind: !picked && item.isBrand === false ? "bespoke" : "brand",
    brand: item.brand ?? "",
    model: item.model ?? "",
    type: item.type ?? "",
    notes: item.notes ?? "",
    value,
    date: utcDay(item.purchasedAt),
    condition: (ITEM_CONDITIONS as readonly string[]).includes(item.condition ?? "")
      ? (item.condition as ItemCondition)
      : DEFAULT_CONDITION,
    accessories: (item.listAccessory ?? []).filter((a): a is Accessory => (ACCESSORIES as readonly string[]).includes(a)),
  };
}

/**
 * `PUT /items` for a legacy edit: the stored item (`withDocuments`) with the
 * edited fields on top. A picked item's brand, model and type stay as
 * stored; accessories the form doesn't know are kept.
 */
export function buildLegacyUpdatePayload(
  item: VaultItem,
  fields: AddItemFields,
  documents: readonly ItemDocument[],
  tenantCurrency: string,
): ItemUpdatePayload {
  const base = withDocuments(item, documents, tenantCurrency);
  const picked = !!item.universalId;
  const branded = picked || fields.brandKind === "brand";
  const unknownAccessories = (item.listAccessory ?? []).filter((a) => !(ACCESSORIES as readonly string[]).includes(a));
  const out: ItemUpdatePayload = {
    ...base,
    valueAmount: parseAmount(fields.value) ?? 0,
    purchasedAt: `${fields.date}T00:00:00.000Z`,
    condition: fields.condition,
    listAccessory: [...unknownAccessories, ...fields.accessories],
    isBrand: branded,
  };
  if (!picked) {
    out.brand = branded ? fields.brand.trim() : "";
    out.model = branded ? fields.model.trim() : "";
    out.type = fields.type.trim();
    if (!branded) out.notes = fields.notes.trim();
  }
  return out;
}

/**
 * `PUT /items` codes that point at a field, and the edit step that owns it:
 * create's codes plus the update-only required codes. A collector item maps
 * them to its `info` section (`collectorUpdateErrorTarget`).
 */
export const UPDATE_ERROR_FIELDS: Record<
  string,
  { step: AddItemStep; field: DetailsField | "photos" | "condition" | "insuredAmount" }
> = {
  ...CREATE_ERROR_FIELDS,
  value_amount_required: { step: "details", field: "value" },
  purchase_date_required: { step: "details", field: "date" },
};

/** A collector edit: the codes that name a field in the `info` section. */
export function collectorUpdateErrorTarget(code: string): { step: "info"; field: string } | undefined {
  switch (code) {
    case "tradingcard_value_exceeds_max":
    case "invalid_value_amount":
    case "value_amount_required":
      return { step: "info", field: "insuredAmount" };
    case "invalid_purchase_date":
    case "purchase_date_required":
      return { step: "info", field: "purchaseDate" };
    default:
      return undefined;
  }
}

/** Every code `PUT /items` / `DELETE /items/:id` can return to the web (each has `errors.<code>`). */
export const UPDATE_ERROR_CODES = [
  ...CREATE_ERROR_CODES,
  "value_amount_required",
  "purchase_date_required",
  "invalid_document_url",
  "item_has_policy",
] as const;

// ---------------------------------------------------------------- drafts

export const DRAFT_PREFIX = "kn.draft.";
export const DRAFT_TTL_MS = 7 * DAY_MS;
export const DRAFT_DEBOUNCE_MS = 500;

export function draftKey(userId: string): string {
  return `${DRAFT_PREFIX}addItem.${userId}`;
}

/** Text fields only: never URLs or file contents. */
export type DraftFields = Pick<
  AddItemFields,
  "entry" | "universalId" | "brandKind" | "brand" | "model" | "type" | "notes" | "value" | "date" | "condition" | "accessories"
> & {
  /** Collector text and choice values. */
  attributes?: CollectorValues;
};

export interface AddItemDraft {
  savedAt: number;
  category: AddCategory | null;
  step: AddItemStep;
  fields: DraftFields;
  /** Names of files chosen before the reload ("Re-attach these files"). */
  fileNames?: string[];
}

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length?: number;
  key?(index: number): string | null;
}

export interface DraftStore {
  load(): AddItemDraft | null;
  save(draft: Omit<AddItemDraft, "savedAt">): void;
  clear(): void;
}

export function toDraftFields(f: AddItemFields): DraftFields {
  return {
    entry: f.entry,
    universalId: f.universalId,
    brandKind: f.brandKind,
    brand: f.brand,
    model: f.model,
    type: f.type,
    notes: f.notes,
    value: f.value,
    date: f.date,
    condition: f.condition,
    accessories: [...f.accessories],
    ...(isCollectorCategory(f.category) ? { attributes: { ...f.attributes } } : {}),
  };
}

function isDraft(v: unknown): v is AddItemDraft {
  if (!v || typeof v !== "object") return false;
  const d = v as Record<string, unknown>;
  return (
    typeof d.savedAt === "number" &&
    typeof d.step === "string" &&
    (d.category === null || isAddCategory(d.category as string)) &&
    (stepsFor(d.category as AddCategory | null) as readonly string[]).includes(d.step) &&
    !!d.fields &&
    typeof d.fields === "object"
  );
}

/** Restore a draft's text fields onto an empty form (unknown values dropped). */
export function fieldsFromDraft(draft: AddItemDraft): AddItemFields {
  const f = draft.fields;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  return {
    ...EMPTY_FIELDS,
    category: draft.category,
    entry: f.entry === "picked" || f.entry === "manual" ? f.entry : null,
    universalId: typeof f.universalId === "string" ? f.universalId : undefined,
    brandKind: f.brandKind === "bespoke" ? "bespoke" : "brand",
    brand: str(f.brand),
    model: str(f.model),
    type: str(f.type),
    notes: str(f.notes),
    value: str(f.value),
    date: str(f.date),
    condition: (ITEM_CONDITIONS as readonly string[]).includes(f.condition) ? f.condition : DEFAULT_CONDITION,
    accessories: Array.isArray(f.accessories)
      ? f.accessories.filter((a): a is Accessory => (ACCESSORIES as readonly string[]).includes(a))
      : [],
    attributes: isCollectorCategory(draft.category) ? sanitizeCollectorValues(f.attributes) : {},
  };
}

/** Per-user add-item draft in localStorage. Every access is in try/catch. */
export function createDraftStore(
  storage: DraftStorage | null,
  userId: string,
  now: () => number = Date.now,
): DraftStore {
  const key = draftKey(userId);
  const remove = () => {
    try {
      storage?.removeItem(key);
    } catch {
      // storage unavailable
    }
  };
  return {
    load() {
      let raw: string | null = null;
      try {
        raw = storage?.getItem(key) ?? null;
      } catch {
        return null;
      }
      if (!raw) return null;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (!isDraft(parsed) || now() - parsed.savedAt > DRAFT_TTL_MS) {
          remove();
          return null;
        }
        return parsed;
      } catch {
        remove();
        return null;
      }
    },
    save(draft) {
      try {
        storage?.setItem(key, JSON.stringify({ ...draft, savedAt: now() }));
      } catch {
        // full or blocked: drafts are a convenience
      }
    },
    clear: remove,
  };
}

/** Delete every `kn.draft.*` key (sign-out and session loss). */
export function clearAllDrafts(storage: DraftStorage | null): void {
  try {
    if (!storage || typeof storage.length !== "number" || !storage.key) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k?.startsWith(DRAFT_PREFIX)) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
  } catch {
    // storage unavailable
  }
}
